/**
 * Painel do próprio usuário, no rodapé da barra de canais.
 *
 * Os controles de microfone e fone ficam sempre visíveis, mesmo fora de uma
 * chamada. Ter que entrar num canal para descobrir que o microfone está mudo é
 * um dos jeitos mais fáceis de irritar quem usa.
 */

import { useState } from 'react'
import { Headphones, Mic, MicOff, Settings, VolumeX } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/store/auth'
import { useVoiceStore } from '@/store/voice'
import { usePreferencesStore } from '@/store/preferences'
import { Avatar, Tooltip } from '@/components/ui'
import { SettingsModal } from '@/components/settings/SettingsModal'

export function UserPanel() {
  const user = useAuthStore((s) => s.user)
  const inCall = useVoiceStore((s) => s.room !== null)
  const muted = useVoiceStore((s) => s.muted)
  const deafened = useVoiceStore((s) => s.deafened)
  const toggleMute = useVoiceStore((s) => s.toggleMute)
  const toggleDeafen = useVoiceStore((s) => s.toggleDeafen)
  const localLevel = useVoiceStore((s) => s.localAudioLevel)

  const startMuted = usePreferencesStore((s) => s.audio.startMuted)
  const setAudio = usePreferencesStore((s) => s.setAudio)

  const [settingsOpen, setSettingsOpen] = useState(false)

  if (!user) return null

  // Fora de uma chamada, os botões editam a preferência de entrada em vez de
  // agir sobre uma sessão que não existe.
  const effectiveMuted = inCall ? muted : startMuted

  const handleMute = () => {
    if (inCall) void toggleMute()
    else setAudio({ startMuted: !startMuted })
  }

  return (
    <>
      <div className="flex h-14 shrink-0 items-center gap-1.5 bg-surface-deepest px-2">
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-hover"
        >
          <div className="relative">
            <Avatar
              userId={user.id}
              name={user.displayName}
              src={user.avatarUrl}
              size="sm"
              status="online"
              ringColor="border-surface-deepest"
            />
            {inCall && !effectiveMuted ? (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-inset ring-speaking transition-opacity duration-75"
                style={{ opacity: Math.min(1, localLevel * 2) }}
              />
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-content-primary">
              {user.displayName}
            </p>
            <p className="truncate text-[11px] text-content-muted">@{user.username}</p>
          </div>
        </button>

        <Tooltip content={effectiveMuted ? 'Ativar microfone' : 'Silenciar microfone'}>
          <button
            type="button"
            onClick={handleMute}
            aria-label={effectiveMuted ? 'Ativar microfone' : 'Silenciar microfone'}
            aria-pressed={effectiveMuted}
            className={cn(
              'flex size-8 items-center justify-center rounded-md transition-colors',
              effectiveMuted
                ? 'text-danger hover:bg-danger-subtle'
                : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
            )}
          >
            {effectiveMuted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
          </button>
        </Tooltip>

        <Tooltip content={deafened ? 'Ativar áudio' : 'Desativar áudio'}>
          <button
            type="button"
            onClick={toggleDeafen}
            disabled={!inCall}
            aria-label={deafened ? 'Ativar áudio' : 'Desativar áudio'}
            aria-pressed={deafened}
            className={cn(
              'flex size-8 items-center justify-center rounded-md transition-colors',
              'disabled:opacity-40',
              deafened
                ? 'text-danger hover:bg-danger-subtle'
                : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
            )}
          >
            {deafened ? <VolumeX className="size-5" /> : <Headphones className="size-5" />}
          </button>
        </Tooltip>

        <Tooltip content="Configurações">
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Configurações"
            className="flex size-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
          >
            <Settings className="size-5" />
          </button>
        </Tooltip>
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  )
}
